import { format, subDays } from 'date-fns'
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { PoratalSiteInfo } from 'schema/contract'
import { formatedDate } from 'utils'

export type CellValue = string | number | null | undefined
export type TableRow = Record<string, CellValue>
export type TableRowWithDate = TableRow & { date: string }
export type MetricRowConfig = {
  key: string
  label: ReactNode
  formatter?: (value: CellValue) => ReactNode
}

type DateInfo = {
  key: string
  label: string
}

export const DAY_COUNT = 30

const createDateListInternal = (): DateInfo[] => {
  const baseDate = subDays(new Date(), 1)
  return Array.from({ length: DAY_COUNT }, (_, index) => {
    const current = subDays(baseDate, index)
    return {
      key: format(current, 'yyyy/MM/dd'),
      label: format(current, 'MM/dd'),
    }
  }).reverse()
}

export const useDailyDates = () => {
  return useMemo(createDateListInternal, [])
}

export const createDateList = createDateListInternal

export const formatCellValue = (value: CellValue) => {
  if (value === null || value === undefined) {
    return '-'
  }

  if (typeof value === 'number') {
    return value.toLocaleString()
  }

  return value
}

export const formatPercent = (value: CellValue) => {
  if (typeof value !== 'number') {
    return '-'
  }

  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`
}

const buildQuantityMap = (list?: PoratalSiteInfo[]) => {
  if (!list?.length) {
    return {}
  }

  return list.reduce<Record<string, number>>((acc, data) => {
    const key = formatedDate(data.date)
    if (key) {
      acc[key] = data.quantity
    }
    return acc
  }, {})
}

export const useQuantityMap = (list?: PoratalSiteInfo[]) => {
  return useMemo(() => buildQuantityMap(list), [list])
}

type PortalDailyDataAccordionProps = {
  rows: TableRowWithDate[]
  metricRows: MetricRowConfig[]
}

const PortalDailyDataAccordion = ({
  rows,
  metricRows,
}: PortalDailyDataAccordionProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const headerScrollRef = useRef<HTMLDivElement>(null)
  const bodyScrollRef = useRef<HTMLDivElement>(null)
  const syncingScrollRef = useRef(false)
  const syncScroll = useCallback((source: 'header' | 'body') => {
    if (syncingScrollRef.current) {
      return
    }

    const sourceEl =
      source === 'header' ? headerScrollRef.current : bodyScrollRef.current
    const targetEl =
      source === 'header' ? bodyScrollRef.current : headerScrollRef.current

    if (!sourceEl || !targetEl) {
      return
    }

    syncingScrollRef.current = true
    targetEl.scrollLeft = sourceEl.scrollLeft
    requestAnimationFrame(() => {
      syncingScrollRef.current = false
    })
  }, [])

  if (!rows.length) {
    return null
  }

  const colGroup = (
    <colgroup>
      <col style={{ width: '110px' }} />
      {rows.map((row) => (
        <col key={row.date} style={{ width: '44px' }} />
      ))}
    </colgroup>
  )

  return (
    <Container>
      <AccordionButton type="button" onClick={() => setIsOpen((prev) => !prev)}>
        <span>日毎のデータをみる</span>
        <ToggleIcon $open={isOpen} />
      </AccordionButton>
      {isOpen && (
        <AccordionContent>
          <TableWrapper>
            <StickyHeader>
              <TableScroll
                ref={headerScrollRef}
                onScroll={() => syncScroll('header')}
              >
                <StyledTable aria-hidden="true">
                  {colGroup}
                  <thead>
                    <tr>
                      <StickyHeadCell>項目</StickyHeadCell>
                      {rows.map((row) => (
                        <HeadCell key={row.date} $align="center" $isMetric>
                          {row.date}
                        </HeadCell>
                      ))}
                    </tr>
                  </thead>
                </StyledTable>
              </TableScroll>
            </StickyHeader>
            <TableScroll
              ref={bodyScrollRef}
              onScroll={() => syncScroll('body')}
            >
              <StyledTable>
                {colGroup}
                <tbody>
                  {metricRows.map((metric) => (
                    <tr key={metric.key}>
                      <StickyLabelCell>{metric.label}</StickyLabelCell>
                      {rows.map((row) => {
                        const value = row[metric.key]
                        return (
                          <BodyCell
                            key={`${metric.key}-${row.date}`}
                            $align="center"
                            $isMetric
                          >
                            {metric.formatter
                              ? metric.formatter(value)
                              : formatCellValue(value)}
                          </BodyCell>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableScroll>
          </TableWrapper>
        </AccordionContent>
      )}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
`

const AccordionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.FONT_COLOR};
  font-size: 16px;
  font-weight: 600;
  padding: 8px 0;
  cursor: pointer;

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    font-size: 14px;
  }
`

const ToggleIcon = styled.span<{ $open: boolean }>`
  border: solid ${(p) => p.theme.FONT_COLOR};
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 4px;
  transform: ${(p) => (p.$open ? 'rotate(225deg)' : 'rotate(45deg)')};
  transition: transform 0.2s ease;
`

const AccordionContent = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${(p) => p.theme.BORDER};
`

const TableWrapper = styled.div`
  width: 100%;
`

const StickyHeader = styled.div`
  position: sticky;
  top: 80px;
  z-index: 1;
  background: ${(p) => p.theme.BACKGROUND_SURFACE};

  @media only screen and (max-width: ${(p) => p.theme.vp}px) {
    top: 60px;
  }
`

const TableScroll = styled.div`
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const StyledTable = styled.table`
  width: max-content;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
  min-width: 720px;
`

const HeadCell = styled.th<{
  $align?: 'left' | 'right' | 'center'
  $isMetric?: boolean
}>`
  z-index: 1;
  text-align: ${(p) => p.$align || 'left'};
  padding: 6px 8px;
  color: ${(p) => p.theme.FONT_COLOR};
  font-weight: 600;
  border-bottom: 1px solid ${(p) => p.theme.BORDER};
  white-space: nowrap;
  min-width: ${(p) => (p.$isMetric ? '44px' : '72px')};
  background: ${(p) => p.theme.BACKGROUND_SURFACE};
`

const LabelCell = styled.td`
  text-align: left;
  padding: 8px 8px;
  border-bottom: 1px solid ${(p) => p.theme.BORDER};
  font-weight: 600;
  color: ${(p) => p.theme.FONT_COLOR};
  white-space: normal;
  min-width: 110px;
`

const StickyHeadCell = styled(HeadCell)`
  position: sticky;
  left: 0;
  z-index: 2;
  width: 110px;
  min-width: 110px;
  box-sizing: border-box;
  background: ${(p) => p.theme.BACKGROUND_SURFACE};
`

const StickyLabelCell = styled(LabelCell)`
  position: sticky;
  left: 0;
  z-index: 0;
  width: 110px;
  min-width: 110px;
  box-sizing: border-box;
  background: ${(p) => p.theme.BACKGROUND_SURFACE};
`

const BodyCell = styled.td<{
  $align?: 'left' | 'right' | 'center'
  $isMetric?: boolean
}>`
  text-align: ${(p) => p.$align || 'left'};
  padding: 8px 8px;
  border-bottom: 1px solid ${(p) => p.theme.BORDER};
  color: ${(p) => p.theme.FONT_COLOR};
  white-space: nowrap;
  min-width: ${(p) => (p.$isMetric ? '44px' : '72px')};
`

export default PortalDailyDataAccordion
